// Lit.dev
import { LitElement, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';

// Styles
import { styles as markdownStyles } from '../styles/markdown'
import { styles as editorStyles } from '../styles/editor'

// Code Mirror
import { basicSetup } from "codemirror"
import { EditorState, Compartment } from "@codemirror/state"
import { markdown } from "@codemirror/lang-markdown"
import { monokai } from '@uiw/codemirror-theme-monokai';
import { Prec } from "@codemirror/state";

import {
  EditorView, ViewPlugin, keymap, highlightSpecialChars, drawSelection,
  highlightActiveLine, dropCursor, rectangularSelection, KeyBinding,
  crosshairCursor, lineNumbers, highlightActiveLineGutter
} from "@codemirror/view"
import {
  defaultHighlightStyle, syntaxHighlighting, indentOnInput,
  bracketMatching, foldGutter, foldKeymap
} from "@codemirror/language"
import {
  defaultKeymap, history, historyKeymap, indentWithTab
} from "@codemirror/commands"
import { NoteItem } from '../modules/note';

@customElement('app-note-editor')

export class AppNoteEditor extends LitElement {
  @property({ type: String }) title = 'Note.md';
  @property({ type: Boolean }) enableBack: boolean = false;
  @property({ type: Boolean }) previewState: boolean = true;
  @property({ type: Object }) activeNote: NoteItem;

  static styles = [
    markdownStyles,
    editorStyles
  ];

  private editorView?: EditorView;
  private previewView?: null;
  private previewContainer?: HTMLElement;
  private editorContainer?: HTMLElement;

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('activeNote')) {
      this.updateEditorContent(this.activeNote);
    }
  }

  updateEditorContent(note) {
    this.editorView.dispatch({changes: {
      from: 0,
      to: this.editorView.state.doc.length,
      insert: note.content
    }})

    this.updatePreview(note.content);

  }

  getKeyMappings() {
    const context = this;
    const map: KeyBinding[] = [
     {
       // Toggle Preview
       key: "Ctrl-Shift-x",
       run: (target) => {
        this.togglePreview();
        return true;
       },
       preventDefault: true, // Prevent default browser behavior
     },
     {
       // Toggle Preview
       key: "Ctrl-Shift-n",
       run: (target) => {
        console.log('...');

        let event = new CustomEvent('events', {
          bubbles: true,
          composed: true,
          detail: {
            action: 'new-note'
          }
        });
        context.dispatchEvent(event);
        return true;
       },
       preventDefault: true, // Prevent default browser behavior
     },
     {
       key: "Mod-b",
       run: (target) => {
        // Your custom logic for bold text
        console.log("Mod-b pressed");
        return true; // Indicate the event was handled
        },
        preventDefault: true, // Prevent default browser behavior
     },
   ];

   return map;
  }

  updatePreview(content) {
    let converter = new showdown.Converter({
      metadata: true
    });
    converter.setFlavor('github');
    let html = converter.makeHtml(content, {
      ghCompatibleHeaderId: true,
      simplifiedAutoLink: true,
      literalMidWordUnderscores: true,
      strikethrough: true,
      tables: true,
      ghCodeBlocks: true,
      tasklists: true,
      openLinksInNewWindow: true,
      backslashEscapesHTMLTags: true,
      emoji: true,
      moreStyling: true
    });

    if (typeof converter.getMetadata === 'function') {
      const metadata = converter.getMetadata();
      if (metadata && typeof metadata === 'object') {
        for (const [key, value] of Object.entries(metadata)) {
          const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
          html = html.replace(regex, value ?? '');
        }
      }
    }

    let event = new CustomEvent('events', {
      bubbles: true,
      composed: true,
      detail: {
        action: 'update-note',
        note: this.activeNote,
        updates: {
          content: content,
          metadata: converter.getMetadata()
        }
      }
    });

    this.dispatchEvent(event);
    this.previewContainer.innerHTML = html;
  }
  firstUpdated() {
    this.editorContainer = this.renderRoot?.getElementById('editor');
    this.previewContainer = this.renderRoot?.getElementById('preview');
    const keyMappings = Prec.highest(keymap.of(this.getKeyMappings()));
    let language = new Compartment, tabSize = new Compartment;

    const state = EditorState.create({
      doc: this.activeNote?.content,
      extensions: [
        monokai,
        keyMappings,
        keymap.of([indentWithTab]),
        basicSetup,
        dropCursor(),
        history(),
        bracketMatching(),
        lineNumbers(),
        highlightSpecialChars(),
        drawSelection(),
        highlightActiveLine(),
        rectangularSelection(),
        crosshairCursor(),
        highlightActiveLineGutter(),
        EditorView?.lineWrapping,
        language.of(markdown()),
        tabSize.of(EditorState.tabSize.of(8))
      ]
    })

    if (this.editorContainer) {
      this.editorView = new EditorView({
        state,
        parent: this.editorContainer,
      });



      this.editorContainer.addEventListener('keyup', (event: KeyboardEvent) => {
        if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          console.log('Save action triggered');
        }

        if (this.editorView) {
          const content = this.editorView.state.doc.toString();
          this.updatePreview(content);
        }

      });
    }
  }

  togglePreview() {
    const previewVisible = !this.previewContainer?.classList.contains('hidden');

    if (!previewVisible) {
      this.previewContainer.classList.remove('hidden');
    } else {
      this.previewContainer.classList.add('hidden');
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('toggle-sidebar', this.previewStateListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.editorView) {
      this.editorView.destroy();
      this.editorView = undefined;
    }
  }

  render() {

    return html`
      <section id="editor" class="note-editor"></section>
      <section id="preview" class="${this.previewState ? '' : 'hidden'} note-preview markdown-body"><textarea style="display:none;">### Hello world!</textarea></section>
    `;
  }
}
